/**
 * Boolean Field Type - Inline Renderers
 *
 * Read-only and editable in-table cell renderers for boolean (True/False) columns.
 */

import type { ColumnDef, CellContext } from '@tanstack/react-table';
import { Chip } from 'components';
import { TableTooltip } from '../../TableTooltip';
import { ChipCell } from '../../ChipCell';
import type { TypeConfig } from '../../../components/types';
import type { ColumnRenderer, RenderOptions, CustomColumn } from '../../columnModel';
import { getCellBooleanValue } from '../cellValue';
import { booleanTypeConfig, resolveFieldConfigFn } from '../boolean';

/**
 * Helper to get boolean config
 */
const getBooleanConfig = (
  colId: string
): { options: TypeConfig['options']; chipColors: TypeConfig['chipColors'] } => {
  const resolved = resolveFieldConfigFn
    ? (resolveFieldConfigFn({ id: colId, type: 'boolean' }) as {
        kind: string;
        options: TypeConfig['options'];
        chipColors: TypeConfig['chipColors'];
      } | null)
    : {
        kind: 'boolean',
        options: booleanTypeConfig.options,
        chipColors: booleanTypeConfig.chipColors,
      };

  if (!resolved || resolved.kind !== 'boolean') {
    throw new Error(`Failed to resolve boolean config for column: ${colId}`);
  }

  return resolved;
};

// ============================================================================
// Read-Only Column Renderer
// ============================================================================

/**
 * Boolean read-only renderer - displays True/False chip
 * Uses col.id as the data field name
 */
export const booleanReadOnlyRenderer = <T extends { id: number | string }>(
  col: CustomColumn<T>,
  _options: RenderOptions<T>
): Partial<ColumnDef<T>> => {
  const { options, chipColors } = getBooleanConfig(col.id);

  return {
    cell: (context: CellContext<T, unknown>) => {
      // Use getCellBooleanValue to respect accessorFn (important for custom fields)
      const value = getCellBooleanValue(context, col.id);
      const option = options.find(o => o.id === value) || options[1];
      const background = value ? chipColors.true : chipColors.false;

      return (
        <TableTooltip title={option.label}>
          <Chip
            sx={{ padding: '4px 16px' }}
            isBordered={false}
            isBackgroundFilled
            isTooltip={false}
            label={option.label}
            color={background}
            background={background}
          />
        </TableTooltip>
      );
    },
  };
};

// ============================================================================
// Editable Column Renderer
// ============================================================================

/**
 * Boolean editable renderer - displays chip with dropdown for editing
 * Uses col.id as the data field name
 */
export const booleanEditableRenderer = <T extends { id: number | string }>(
  col: CustomColumn<T>,
  _options: RenderOptions<T>
): Partial<ColumnDef<T>> => {
  const { options: booleanOptions, chipColors } = getBooleanConfig(col.id);
  const comboBoxOptions = booleanOptions.map(o => ({
    id: String(o.id),
    label: o.label,
  }));

  return {
    cell: (context: CellContext<T, unknown>) => {
      const value = getCellBooleanValue(context, col.id);
      const option = booleanOptions.find(o => o.id === value) || booleanOptions[1];
      const background = value ? chipColors.true : chipColors.false;

      return (
        <ChipCell
          context={context}
          label={option.label}
          background={background}
          color={background}
          editable
          options={comboBoxOptions}
          onUpdate={selected => {
            if (col.onUpdate) {
              const boolValue = selected.id === 'true';
              col.onUpdate(context.row.original, { [col.id]: boolValue } as Partial<T>);
            }
          }}
        />
      );
    },
    meta: {
      editable: true,
    },
  };
};

// ============================================================================
// Typed Renderer Exports (for registry)
// ============================================================================

/** Read-only renderer with proper typing */
export const booleanReadOnly: ColumnRenderer = booleanReadOnlyRenderer;

/** Editable renderer with proper typing */
export const booleanEditable: ColumnRenderer = booleanEditableRenderer;
